/**
 * Segment Service
 *
 * Manages recording segments for progressive upload.
 * Handles preprocessing (silence removal) and uploading to S3.
 *
 * S3 Structure:
 * inspections/{user_id}/{inspection_id}/
 * ├── 2025_01_27_18_03_49_recording.webm    # Segment 1
 * ├── 2025_01_27_18_08_49_recording.webm    # Segment 2
 * ├── final.webm                             # Concatenated (backend via finalize API)
 * └── transcription.json                     # Transcript (backend)
 */

import { getPresignedUrl, uploadToS3, createInspection, captureDeviceInfo, updateSegmentMetadata, reportSegmentError } from './uploadService';
import {
  storeChunk,
  getChunksForRecording,
  deleteRecordingData,
  updateChunkStatus,
  ChunkStatus
} from './chunkStorage';
import { analyzeAudio, removeSilence } from './audioProcessingService';
import { settings } from './settingsService';
import { getInputDeviceName } from './microphoneService';

// Session storage key for current inspection
const CURRENT_INSPECTION_KEY = 'currentInspection';

// Track segments per inspection in memory (backed by IndexedDB)
const sessionSegments = new Map();

// Track when sessions were last accessed (for stale session cleanup)
const sessionTimestamps = new Map();

// Max age for in-memory session data before cleanup (24 hours)
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000;

// Lock to prevent concurrent inspection creation (React StrictMode double-mount protection)
let inspectionCreationPromise = null;

/**
 * Clean up stale in-memory sessions to prevent unbounded memory growth
 * Sessions older than MAX_SESSION_AGE_MS are removed
 */
const cleanupStaleSessions = () => {
  const now = Date.now();
  let cleanedCount = 0;
  for (const [id, timestamp] of sessionTimestamps) {
    if (now - timestamp > MAX_SESSION_AGE_MS) {
      sessionSegments.delete(id);
      sessionTimestamps.delete(id);
      cleanedCount++;
    }
  }
  if (cleanedCount > 0) {
    console.log(`[segmentService] Cleaned up ${cleanedCount} stale session(s)`);
  }
};

/**
 * Generate timestamp string for segment naming (YYYY_MM_DD_HH_MM_SS format)
 * @returns {string} Formatted timestamp
 */
const generateTimestamp = () => {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${yyyy}_${mm}_${dd}_${hh}_${min}_${ss}`;
};

/**
 * Get existing or create new inspection ID for a recording session
 *
 * If an inspection already exists for the given vehicleId, returns that inspection ID.
 * Otherwise, creates a new inspection via the backend API.
 *
 * @param {string|null} vehicleId - Vehicle identifier (optional)
 * @param {string|null} title - Display title for the inspection (optional, auto-generated if not provided)
 * @returns {Promise<string>} Inspection ID
 */
export const getOrCreateInspection = async (vehicleId, title = null) => {
  // Check localStorage for existing session
  const existing = localStorage.getItem(CURRENT_INSPECTION_KEY);
  console.log('[getOrCreateInspection] Input vehicleId:', vehicleId, 'title:', title);
  console.log('[getOrCreateInspection] localStorage raw:', existing);

  if (existing) {
    try {
      const data = JSON.parse(existing);
      console.log('[getOrCreateInspection] Parsed data:', data);

      // If there's an existing inspection in the session, use it regardless of vehicleId changes
      // VehicleId updates are handled separately via PATCH API
      if (data.inspectionId) {
        console.log(`Using existing inspection: ${data.inspectionId} (vehicleId may have changed)`);
        // Update timestamp on access to keep session alive
        sessionTimestamps.set(data.inspectionId, Date.now());
        return data.inspectionId;
      }
      console.log('[getOrCreateInspection] No inspectionId in session - creating new inspection');
    } catch (e) {
      console.warn('Failed to parse existing inspection data:', e);
    }
  } else {
    console.log('[getOrCreateInspection] No existing session in localStorage');
  }

  // If there's already an in-flight creation, wait for it (React StrictMode protection)
  if (inspectionCreationPromise) {
    console.log('[getOrCreateInspection] Waiting for in-flight creation...');
    return inspectionCreationPromise;
  }

  // Create new inspection via backend API
  console.log('[getOrCreateInspection] Creating NEW inspection for:', vehicleId, 'title:', title);

  // Wrap creation in a promise and store it to prevent concurrent calls
  inspectionCreationPromise = (async () => {
    try {
      // Capture device info for analytics
      const inputDeviceName = getInputDeviceName();
      const deviceInfo = captureDeviceInfo(inputDeviceName);

      const inspection = await createInspection(vehicleId || null, title, deviceInfo);
      const inspectionId = inspection.inspectionId;

      // Store in localStorage for session persistence
      localStorage.setItem(CURRENT_INSPECTION_KEY, JSON.stringify({
        inspectionId,
        vehicleId: vehicleId || '',
        title: inspection.title,
        createdAt: new Date().toISOString()
      }));

      // Initialize in-memory segment tracking with timestamp
      sessionSegments.set(inspectionId, []);
      sessionTimestamps.set(inspectionId, Date.now());

      // Clean up stale sessions periodically (on new session creation)
      cleanupStaleSessions();

      console.log(`Created new inspection: ${inspectionId} with title: ${inspection.title}`);
      return inspectionId;
    } finally {
      // Clear the lock after completion (success or failure)
      inspectionCreationPromise = null;
    }
  })();

  return inspectionCreationPromise;
};

/**
 * Preprocess audio (silence removal) and upload a segment to S3
 *
 * @param {string} inspectionId - Inspection ID from getOrCreateInspection
 * @param {Blob} audioBlob - Raw audio blob to process and upload
 * @param {function} onProgress - Progress callback: (stage: 'analyzing'|'preprocessing'|'uploading'|'complete') => void
 * @param {number|null} existingChunkId - If provided, update this chunk instead of creating new one
 * @param {string|null} originalFilename - If provided (for retry), use this filename instead of generating new one
 * @returns {Promise<object>} Uploaded segment info
 */
export const processAndUploadSegment = async (inspectionId, audioBlob, onProgress, existingChunkId = null, originalFilename = null) => {
  console.log(`[processAndUploadSegment] RECEIVED audioBlob.size=${audioBlob.size}, type=${audioBlob.type}, existingChunkId=${existingChunkId}, originalFilename=${originalFilename}`);

  // Use original filename if provided (for retry), otherwise generate new timestamp
  const timestamp = originalFilename ? null : generateTimestamp();
  const filename = originalFilename || `${timestamp}_recording`;  // No extension - backend adds it based on mimeType
  const segmentIndex = (sessionSegments.get(inspectionId) || []).length;

  let rawS3Key = null;

  try {
    // Step 0: Upload raw chunk if debug setting is enabled (no side effects at backend)
    if (settings.uploadRawChunks.isEnabled()) {
      try {
        const rawFilename = `${timestamp}_raw`;
        const { uploadUrl: rawUploadUrl, key: rawKey } = await getPresignedUrl(
          inspectionId,
          rawFilename,
          audioBlob.type || 'audio/webm'
        );
        await uploadToS3(rawUploadUrl, audioBlob);
        rawS3Key = rawKey;
        console.log(`Raw chunk uploaded: ${rawFilename}`);
      } catch (rawError) {
        // Don't fail the main upload if raw upload fails
        console.warn('Raw chunk upload failed (continuing with preprocessing):', rawError);
      }
    }

    // Step 1: Analyze audio for silence
    onProgress?.('analyzing');
    const analysis = await analyzeAudio(audioBlob);

    // Step 2: Preprocess (silence removal)
    onProgress?.('preprocessing');
    const { blob: processedBlob, stats } = await removeSilence(audioBlob, analysis);

    // Step 3: Get presigned URL
    onProgress?.('uploading');
    const { uploadUrl, key: s3Key } = await getPresignedUrl(
      inspectionId,
      filename,
      processedBlob.type || 'audio/webm'
    );

    // Step 4: Upload to S3
    await uploadToS3(uploadUrl, processedBlob);

    // Step 4.5: Update segment metadata in backend (duration, silence removed)
    // Extract filename with extension from s3Key (e.g., '2026_02_03_15_53_29_recording.webm')
    const filenameWithExt = s3Key.split('/').pop();
    console.log('[processAndUploadSegment] Calling updateSegmentMetadata:', {
      inspectionId,
      s3Key,
      filenameWithExt,
      stats: { processedDuration: stats.processedDuration, originalDuration: stats.originalDuration, silenceRemoved: stats.silenceRemoved }
    });
    await updateSegmentMetadata(inspectionId, filenameWithExt, {
      durationSec: stats.processedDuration || 0,
      originalDurationSec: stats.originalDuration || 0,
      silenceRemovedSec: stats.silenceRemoved || 0
    });
    console.log('[processAndUploadSegment] updateSegmentMetadata completed');

    // Step 5: Track segment - either update existing chunk or create new one
    let chunkId;

    if (existingChunkId) {
      // Update existing chunk with processed data (single-source-of-truth pattern)
      await updateChunkStatus(existingChunkId, ChunkStatus.UPLOADED, {
        blob: processedBlob,  // Replace raw blob with processed blob
        blobType: processedBlob.type || 'audio/webm',
        s3Key,
        rawS3Key,
        filename,
        duration: stats.processedDuration || 0,
        originalDuration: stats.originalDuration,
        silenceRemoved: stats.silenceRemoved,
        uploadedAt: Date.now()
      });
      chunkId = existingChunkId;
      console.log(`Chunk ${existingChunkId} updated with processed data`);
    } else {
      // Create new chunk (for file uploads, retries, etc.)
      const segment = {
        recordingId: inspectionId,
        chunkIndex: segmentIndex,
        blob: processedBlob,
        mimeType: processedBlob.type || 'audio/webm',
        duration: stats.processedDuration || 0,
        timestamp: Date.now(),
        filename,
        s3Key,
        rawS3Key,
        originalDuration: stats.originalDuration,
        silenceRemoved: stats.silenceRemoved,
        status: ChunkStatus.UPLOADED,
        uploadedAt: new Date().toISOString()
      };

      // Store in IndexedDB for persistence
      chunkId = await storeChunk(segment);

      // Update status to UPLOADED with all additional metadata
      await updateChunkStatus(chunkId, ChunkStatus.UPLOADED, {
        s3Key,
        rawS3Key,
        filename: segment.filename,
        originalDuration: stats.originalDuration,
        silenceRemoved: stats.silenceRemoved,
        uploadedAt: Date.now()
      });
    }

    // Update in-memory tracking (exclude blob to save memory)
    const uploadedAt = new Date().toISOString();
    const segments = sessionSegments.get(inspectionId) || [];
    segments.push({
      filename,
      s3Key,
      rawS3Key,
      chunkIndex: segmentIndex,
      duration: stats.processedDuration || 0,
      originalDuration: stats.originalDuration,
      id: chunkId,
      status: ChunkStatus.UPLOADED,
      uploadedAt,
      mimeType: processedBlob.type || 'audio/webm',
      silenceRemoved: stats.silenceRemoved
    });
    sessionSegments.set(inspectionId, segments);

    onProgress?.('complete');

    console.log(`Segment ${segmentIndex} uploaded: ${s3Key}`);
    return {
      filename,
      s3Key,
      timestamp,
      duration: stats.processedDuration,
      originalDuration: stats.originalDuration,
      silenceRemoved: stats.silenceRemoved,
      uploadedAt,
      status: 'uploaded'
    };

  } catch (error) {
    console.error(`Segment upload failed: ${error.message}`, error);

    // Create detailed error message with context
    const errorTimestamp = new Date().toISOString();
    let detailedError = error.message || 'Unknown error';

    // Add context based on common failure modes
    if (error.message?.includes('Network')) {
      detailedError = `Network error: ${error.message}. Check internet connection.`;
    } else if (error.message?.includes('presigned') || error.message?.includes('URL')) {
      detailedError = `Failed to get upload URL: ${error.message}. API may be unavailable.`;
    } else if (error.message?.includes('S3') || error.message?.includes('upload')) {
      detailedError = `S3 upload failed: ${error.message}. Storage may be unavailable.`;
    } else if (error.message?.includes('audio') || error.message?.includes('Audio')) {
      detailedError = `Audio processing failed: ${error.message}. Audio format may be unsupported.`;
    } else if (error.name === 'AbortError') {
      detailedError = 'Upload was aborted. Tab may have been backgrounded or closed.';
    } else if (error.name === 'TimeoutError') {
      detailedError = 'Upload timed out. Network may be slow or unstable.';
    }

    // Store failed segment with detailed error
    const failedSegment = {
      recordingId: inspectionId,
      chunkIndex: segmentIndex,
      blob: audioBlob,
      mimeType: audioBlob.type || 'audio/webm',
      timestamp: Date.now(),
      filename,
      status: ChunkStatus.FAILED,
      error: detailedError,
      errorTimestamp,
      retryCount: 0
    };
    await storeChunk(failedSegment);

    // Sync error to backend (best-effort, don't block on this)
    // Use a placeholder filename if upload URL wasn't obtained yet
    const filenameForBackend = `${filename}_recording.webm`;
    reportSegmentError(inspectionId, filenameForBackend, detailedError).catch(err => {
      console.warn('[processAndUploadSegment] Failed to sync error to backend:', err.message);
    });

    throw error;
  }
};

/**
 * Get segment upload statistics for UI display
 *
 * @param {string} inspectionId - Inspection ID
 * @returns {Promise<object>} Segment statistics
 */
export const getSegmentStats = async (inspectionId) => {
  const segments = await getChunksForRecording(inspectionId);

  const uploaded = segments.filter(s => s.status === ChunkStatus.UPLOADED).length;
  const failed = segments.filter(s => s.status === ChunkStatus.FAILED).length;
  const pending = segments.filter(s => s.status === ChunkStatus.PENDING).length;
  const uploading = segments.filter(s => s.status === ChunkStatus.UPLOADING).length;
  const total = segments.length;

  const totalDuration = segments
    .filter(s => s.status === ChunkStatus.UPLOADED)
    .reduce((sum, s) => sum + (s.duration || 0), 0);

  const totalSilenceRemoved = segments
    .filter(s => s.status === ChunkStatus.UPLOADED && s.silenceRemoved)
    .reduce((sum, s) => sum + s.silenceRemoved, 0);

  return {
    uploaded,
    failed,
    pending,
    uploading,
    total,
    totalDuration,
    totalSilenceRemoved,
    allUploaded: uploaded === total && failed === 0 && total > 0,
    hasFailures: failed > 0
  };
};

/**
 * Retry failed segments for a given inspection
 *
 * @param {string} inspectionId - Inspection ID
 * @param {function} onProgress - Progress callback
 * @returns {Promise<{retried: number, stillFailed: number}>}
 */
export const retryFailedSegments = async (inspectionId, onProgress) => {
  const segments = await getChunksForRecording(inspectionId);
  const failed = segments.filter(
    s => s.status === ChunkStatus.FAILED && s.retryCount < 3
  );

  let retried = 0;
  let stillFailed = 0;

  for (const segment of failed) {
    onProgress?.(`retrying_${segment.chunkIndex}`);
    try {
      // Get blob from IndexedDB if available
      const blob = segment.blob
        ? new Blob([segment.blob], { type: segment.mimeType })
        : null;

      if (blob) {
        await processAndUploadSegment(inspectionId, blob, onProgress);
        retried++;
      } else {
        console.warn(`No blob available for retry: segment ${segment.chunkIndex}`);
        stillFailed++;
      }
    } catch (error) {
      console.error(`Retry failed for segment ${segment.chunkIndex}:`, error);
      // Update retry count
      await updateChunkStatus(segment.id, ChunkStatus.FAILED);
      stillFailed++;
    }
  }

  return { retried, stillFailed };
};

// Track segments currently being retried to prevent duplicate retries
const retryingSegments = new Set();

/**
 * Retry a single stale segment (PENDING/UPLOADING for too long)
 *
 * Called automatically from the polling loop when stale segments are detected.
 * Uses a lock to prevent concurrent retries of the same segment.
 *
 * @param {string} inspectionId - Inspection ID
 * @param {number} chunkId - Chunk ID from IndexedDB
 * @param {function} onProgress - Progress callback
 * @returns {Promise<boolean>} True if retry succeeded, false otherwise
 */
export const retryStaleSegment = async (inspectionId, chunkId, onProgress) => {
  // Prevent concurrent retries of the same segment
  if (retryingSegments.has(chunkId)) {
    console.log(`[retryStaleSegment] Segment ${chunkId} already being retried, skipping`);
    return false;
  }

  retryingSegments.add(chunkId);
  console.log(`[retryStaleSegment] Auto-retrying stale segment ${chunkId} for inspection ${inspectionId}`);

  try {
    // Get the segment from IndexedDB
    const segments = await getChunksForRecording(inspectionId);
    const segment = segments.find(s => s.id === chunkId);

    if (!segment) {
      console.warn(`[retryStaleSegment] Segment ${chunkId} not found in IndexedDB`);
      return false;
    }

    // Check if segment has a blob to retry
    if (!segment.blob) {
      console.warn(`[retryStaleSegment] No blob available for segment ${chunkId}, marking as FAILED`);
      await updateChunkStatus(chunkId, ChunkStatus.FAILED, {
        error: 'No audio data available for retry. Recording may have been lost.',
        errorTimestamp: new Date().toISOString()
      });
      return false;
    }

    // Reconstruct blob from stored data
    const blob = new Blob([segment.blob], { type: segment.mimeType || 'audio/webm' });

    // Get original filename to preserve the recording timestamp
    // Filename is stored without extension (e.g., "2025_01_27_10_58_26_recording")
    // If filename isn't set yet, derive it from the chunk's timestamp
    let originalFilename = segment.filename || null;
    if (!originalFilename && segment.timestamp) {
      // Generate filename from original timestamp (same format as generateTimestamp)
      const date = new Date(segment.timestamp);
      const yyyy = String(date.getFullYear());
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      const ss = String(date.getSeconds()).padStart(2, '0');
      originalFilename = `${yyyy}_${mm}_${dd}_${hh}_${min}_${ss}_recording`;
      console.log(`[retryStaleSegment] Generated filename from timestamp ${segment.timestamp}: ${originalFilename}`);
    }
    console.log(`[retryStaleSegment] Using filename: ${originalFilename}`);

    // Mark as UPLOADING before retry
    await updateChunkStatus(chunkId, ChunkStatus.UPLOADING, {
      retryTimestamp: Date.now()
    });

    // Process and upload using the existing chunk ID and original filename (will update in place)
    await processAndUploadSegment(inspectionId, blob, onProgress, chunkId, originalFilename);
    console.log(`[retryStaleSegment] Successfully retried segment ${chunkId}`);
    return true;

  } catch (error) {
    console.error(`[retryStaleSegment] Retry failed for segment ${chunkId}:`, error);

    // Mark as failed with error details
    const errorMessage = `Auto-retry failed: ${error.message || 'Unknown error'}`;
    await updateChunkStatus(chunkId, ChunkStatus.FAILED, {
      error: errorMessage,
      errorTimestamp: new Date().toISOString()
    });

    // Sync error to backend
    try {
      const segments = await getChunksForRecording(inspectionId);
      const segment = segments.find(s => s.id === chunkId);
      if (segment?.filename) {
        await reportSegmentError(inspectionId, `${segment.filename}.webm`, errorMessage);
      }
    } catch (syncError) {
      console.warn('[retryStaleSegment] Failed to sync error to backend:', syncError.message);
    }

    return false;
  } finally {
    retryingSegments.delete(chunkId);
  }
};

/**
 * Clear current recording session
 *
 * Use this when the user cancels a recording or wants to start fresh.
 *
 * @param {string} inspectionId - Optional inspection ID to clear
 * @returns {Promise<void>}
 */
export const clearSession = async (inspectionId) => {
  localStorage.removeItem(CURRENT_INSPECTION_KEY);
  if (inspectionId) {
    await deleteRecordingData(inspectionId);
    sessionSegments.delete(inspectionId);
    console.log(`Session cleared for inspection ${inspectionId}`);
  }
};

/**
 * Get current inspection from session storage
 *
 * @returns {object|null} Current inspection info or null
 */
export const getCurrentInspection = () => {
  const existing = localStorage.getItem(CURRENT_INSPECTION_KEY);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Check if there is an active recording session
 *
 * @returns {boolean} True if there's an active session
 */
export const hasActiveSession = () => {
  return getCurrentInspection() !== null;
};

/**
 * Restore session for an existing PENDING inspection
 *
 * Used when resuming an inspection from the list view.
 *
 * @param {string} inspectionId - Inspection ID to restore
 * @param {string} vehicleId - Vehicle ID for the inspection
 * @param {string} title - Display title for the inspection
 */
export const restoreSession = (inspectionId, vehicleId, title = null) => {
  localStorage.setItem(CURRENT_INSPECTION_KEY, JSON.stringify({
    inspectionId,
    vehicleId,
    title,
    createdAt: new Date().toISOString()
  }));
  console.log(`Session restored for inspection: ${inspectionId}, title: ${title}`);
};
